import { Button } from '../../ui'
import { ExchangeRoleSteps } from '../ExchangeRoleSteps'
import { WILL_ASSESS, WILL_NOT, WILL_NOT_ASSESS } from '../flow'

export interface PermissionsProps {
  onContinue: () => void
  /** On the way to Microsoft. Each press creates a consent request, so a second one must not land. */
  busy?: boolean
}

/**
 * The trust screen, shown before Microsoft's consent page rather than after it.
 *
 * Every line on it is a promise, so every line has to be true of the product today, not of the
 * design. It used to promise Purview, Sentinel and Azure configuration (none assessed), describe the
 * access as "read-only and scoped to least privilege" (Exchange.ManageAsApp is not read-only by name,
 * and Directory.Read.All is not narrow), and say it would not "modify security settings without
 * authorization" -- which says it would with. It also left out the one real extra step, the Exchange
 * directory role, so a customer found it only as a column of Email checks that could not be read.
 *
 * Now: what is assessed, what is not, what Defenssive will never do, and the Exchange step as an
 * instruction. The Microsoft consent screen is still the authority on what is granted, and says so.
 *
 * LAID OUT SO THE NEXT STEP IS FINDABLE (owner, 2026-10-08: "Continue to Microsoft is buried", the
 * text "not aligned"). The button sits in a bar that stays at the bottom of the window while the
 * lists are read; the Exchange steps are folded behind one line that says what they are, and stay in
 * Settings; every line is left-aligned to one measure. No promise was removed: each statement the
 * tests pin is still on the page.
 */
export function Permissions({ onContinue, busy = false }: PermissionsProps) {
  return (
    <div className="ob-screen ob-screen-permissions perm rise-in">
      <h2>Connect Microsoft 365</h2>
      <p className="perm-lede">
        Defenssive reads your Microsoft 365 security settings and reports where they fall short of
        the CISA SCuBA baselines or our own checks. It only reads and changes nothing in your
        tenant. Microsoft shows every permission on its own consent screen, and nothing is granted
        until you approve it there.
      </p>

      <div className="split-columns perm-columns">
        <div>
          <h6 style={{ marginBottom: 'var(--space-4)' }}>We assess</h6>
          <div className="tick-list">
            {WILL_ASSESS.map((item) => (
              <div key={item.name} className="tick tick-yes">
                <span className="tick-mark" aria-hidden="true">
                  ✓
                </span>
                <span>
                  {item.name}
                  <span className="sso-note">{item.reads}</span>
                  {item.needsExchangeRole ? (
                    <span className="sso-note exchange-role-flag">Needs the Exchange role below</span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
          <p className="form-footnote perm-note">
            Each finding says whether it comes from a CISA SCuBA baseline or is one of our own
            checks. A service your Microsoft plan does not include is shown as not assessed, never
            as passing.
          </p>
        </div>

        <div className="split-rule" aria-hidden="true" />

        <div>
          <h6 style={{ marginBottom: 'var(--space-4)' }}>We do not assess</h6>
          <div className="tick-list will-not">
            {WILL_NOT_ASSESS.map((item) => (
              <div key={item} className="tick tick-no">
                <span className="tick-mark" aria-hidden="true">
                  ✕
                </span>
                <span>{item}</span>
              </div>
            ))}
          </div>
          {/* It said "Nor anything else that is not listed under We assess". The lines under We
              assess summarise each service rather than list every check in it, so read together
              the two said that what a summary leaves out is not read -- Defender's vulnerabilities
              and recommendations, once. What is true of the whole page is a statement about
              services. */}
          <p className="form-footnote perm-note">
            Nor any other service that is not listed under We assess.
          </p>

          <h6 style={{ margin: 'var(--space-6) 0 var(--space-4)' }}>We will not</h6>
          <div className="tick-list will-not">
            {WILL_NOT.map((item) => (
              <div key={item} className="tick tick-no">
                <span className="tick-mark" aria-hidden="true">
                  ✕
                </span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="perm-exchange" aria-labelledby="exchange-role-heading">
        <h6 id="exchange-role-heading">After you approve: one more step for Exchange Online</h6>
        <p className="exchange-role-lead">
          Exchange Online and Defender for Office 365 also need a directory role assigned to the
          Defenssive application, and consent cannot assign one. An administrator does it once,
          after approving. These steps are also in Settings, under Microsoft tenant.
        </p>
        <details className="perm-details">
          <summary>Show the three steps</summary>
          <ExchangeRoleSteps />
        </details>
      </section>

      <div className="action-row perm-actions">
        <Button variant="primary" onClick={onContinue} disabled={busy}>
          {busy ? 'Taking you to Microsoft…' : 'Continue to Microsoft'}
        </Button>
        <span className="action-note">
          You&rsquo;ll sign in with Microsoft — we never see your password.
        </span>
      </div>
    </div>
  )
}
